import React, { Fragment, useState, useEffect } from 'react';
import { Input } from 'reactstrap';
import { ApiRequest, ButtonBuilder, ToastBuilder } from '../../../../../../../../GlobalComponents/Builders';
import { showRelativeTime } from '../../../../../../../../GlobalComponents/Helpers';
import useConfirmModal from '../../../../../../../../GlobalComponents/Builders/ConfirmModal';

/**
 * RestrictAccessModal — Manage which email domains are allowed to see this job.
 *
 * Visibility rule (enforced server-side in api-get-handler, DMT tool only):
 *   - Admins and the partner's primary-domain users always see the job.
 *   - A job with NO allowed-domain rows is unrestricted (visible to everyone).
 *   - Once one or more domains are added, only users on those domains (plus
 *     admins / primary-domain users) can see the job.
 *
 * Backs the job_domain_access table via explicit DMT endpoints:
 *   GET    /dmt/jobs/{jobId}/domains
 *   POST   /dmt/jobs/{jobId}/domains          { allowedDomain }
 *   DELETE /dmt/jobs/{jobId}/domains/{allowedDomain}
 */
const RestrictAccessModal = ({ jobData, isArchived }) => {

    const { confirm, ConfirmModal } = useConfirmModal();

    const jobId = jobData?.id || jobData?.job_id;

    const [domains, setDomains] = useState([]);
    const [loading, setLoading] = useState(false);
    const [newDomain, setNewDomain] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const fetchDomains = () => {
        if (!jobId) return;
        setLoading(true);
        ApiRequest('GET', '/dmt/jobs/{jobId}/domains', {
            pathParams: { jobId },
            toastOnError: 'Failed to load domain restrictions',
        }).then((res) => {
            // The get handler returns the body as { records: [...], code },
            // so the rows are under res.data.records (with a bare-array fallback
            // to match how other DMT modals read responses).
            const rows = res.data?.records ?? res.data;
            setDomains(res.success && Array.isArray(rows) ? rows : []);
            setLoading(false);
        });
    };

    useEffect(() => {
        fetchDomains();
    }, [jobId]);

    const handleAdd = () => {
        const allowedDomain = newDomain.trim().toLowerCase();
        if (!allowedDomain) return ToastBuilder('error', 'Enter a domain');
        // Light client-side sanity check (server normalizes/validates too).
        if (!/^[a-z0-9.-]+\.[a-z]{2,}$/.test(allowedDomain)) {
            return ToastBuilder('error', 'Enter a valid domain, e.g. example.com');
        }

        ApiRequest('POST', '/dmt/jobs/{jobId}/domains', {
            pathParams: { jobId },
            body: { allowedDomain },
            setIsProcessing: setSubmitting,
            toastOnSuccess: 'Domain access granted',
            toastOnError: true,
        }).then((res) => {
            if (res.success) {
                setNewDomain('');
                fetchDomains();
            }
        });
    };

    const handleRemove = async (allowedDomain) => {
        const confirmed = await confirm(
            `Revoke access for "${allowedDomain}"? Users on this domain will no longer see this job.`,
            { title: 'Revoke Domain Access', confirmLabel: 'Revoke', cancelLabel: 'Cancel' }
        );
        if (!confirmed) return;

        ApiRequest('DELETE', '/dmt/jobs/{jobId}/domains/{allowedDomain}', {
            pathParams: { jobId, allowedDomain },
            toastOnSuccess: 'Domain access revoked',
            toastOnError: true,
        }).then((res) => {
            if (res.success) fetchDomains();
        });
    };

    return (
        <Fragment>
            <p style={{ fontSize: 12, color: '#666', marginBottom: 12 }}>
                Restrict which email domains can see this job. Admins and the partner's primary
                domain always have access. With <strong>no domains listed</strong>, the job is
                unrestricted and visible to everyone. Adding a domain limits visibility to that
                domain (plus admins / primary domain).
            </p>

            {loading ? (
                <p style={{ textAlign: 'center', padding: 20, color: '#999' }}>Loading...</p>
            ) : domains.length === 0 ? (
                <p style={{ textAlign: 'center', padding: 16, color: '#999' }}>
                    No domain restrictions — this job is visible to everyone.
                </p>
            ) : (
                <div style={{ marginBottom: 16, maxHeight: 260, overflowY: 'auto' }}>
                    {domains.map((d) => (
                        <div key={d.allowed_domain} className='d-flex justify-content-between align-items-center' style={{
                            padding: '8px 12px', marginBottom: 6,
                            borderLeft: '3px solid rgba(128,128,128,0.2)',
                            background: 'rgba(128,128,128,0.02)', borderRadius: 2
                        }}>
                            <div style={{ minWidth: 0 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, wordBreak: 'break-word' }}>{d.allowed_domain}</div>
                                {d.created_at ? (
                                    <div style={{ fontSize: 10, color: '#999', marginTop: 2 }}>{showRelativeTime(d.created_at)}</div>
                                ) : null}
                            </div>
                            {!isArchived && (
                                <ButtonBuilder label='Revoke' className='btn-outline-danger btn-sm' color={null}
                                    onClick={() => handleRemove(d.allowed_domain)} useSpinner={false} />
                            )}
                        </div>
                    ))}
                </div>
            )}

            {!isArchived && (
                <div style={{ borderTop: '1px solid rgba(128,128,128,0.2)', paddingTop: 12 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 8 }}>Add Allowed Domain</span>
                    <div className='d-flex gap-2'>
                        <Input bsSize='sm' value={newDomain}
                            onChange={(e) => setNewDomain(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }}
                            placeholder='example.com' />
                        <ButtonBuilder label='Add' className='btn-primary btn-sm px-3' color={null}
                            onClick={handleAdd} useSpinner={submitting} />
                    </div>
                </div>
            )}

            <ConfirmModal />
        </Fragment>
    );
};

export default RestrictAccessModal;
